import lucene, { AST } from 'lucene'
import { SearchOptions } from '@trustero/trustero-api-web/lib/search/search_pb'
import {
  AdvancedSearchOptions,
  SearchableData,
  SearchASTNode,
  SearchOperator,
} from './search.types'

export const prepareSearchableData = <T>(
  rawItems: T[],
  getId: (item: T) => string,
  getSearchableFields: (item: T) => (string | number | null | undefined)[],
  getContentIds?: (item: T) => string[] | undefined,
): SearchableData<T>[] => {
  return rawItems.map((item) => {
    const id = getId(item)
    const searchableText = getSearchableFields(item)
      .filter((val) => val !== null && val !== undefined)
      .map((val) => String(val).toLowerCase())
      .join(' ')
    const result: SearchableData<T> = { id, original: item, searchableText }
    if (getContentIds) {
      result.contentIds = getContentIds(item)
    }
    return result
  })
}

export const buildGroup = (
  words: string[],
  operator: SearchOperator,
  optionsCount?: number,
  prefix?: string,
): SearchASTNode | null => {
  if (!words.length) return null

  let node: SearchASTNode = { term: words[0] }
  if (prefix) node.prefix = prefix // "-" for NOT

  for (let i = 1; i < words.length; i++) {
    if (words[i] === '') continue
    const next: SearchASTNode = { term: words[i] }
    if (prefix) next.prefix = prefix

    node = {
      left: node,
      operator: operator,
      right: next,
      parenthesized:
        optionsCount && optionsCount > 1 && words.length > 1 ? true : false,
    }
  }
  return node
}

export const buildExactMatchNode = (
  exactMatch: string,
): SearchASTNode | null => {
  if (!exactMatch || !exactMatch.trim()) return null
  return { term: exactMatch, quoted: true, regex: false }
}

export const getSearchOptionsCount = (
  searchOptions: AdvancedSearchOptions,
): number => {
  let count = 0
  if (searchOptions.exactMatch?.trim()) {
    count += 1
  }
  if (searchOptions.includeAny?.trim()) {
    count += 1
  }
  if (searchOptions.includeAll?.trim()) {
    count += 1
  }
  if (searchOptions.excludeAll?.trim()) {
    count += 1
  }
  return count
}

export const buildAstFromInputs = (
  searchOptions: AdvancedSearchOptions,
): SearchASTNode | null => {
  const hasWordsArr = searchOptions.includeAny.trim()
    ? searchOptions.includeAny.trim().split(/\s+/)
    : []
  const hasAllWordsArr = searchOptions.includeAll.trim()
    ? searchOptions.includeAll.trim().split(/\s+/)
    : []
  const notWordsArr = searchOptions.excludeAll.trim()
    ? searchOptions.excludeAll.trim().split(/\s+/)
    : []
  const exactMatch = buildExactMatchNode(searchOptions.exactMatch)

  const optionsCount = getSearchOptionsCount(searchOptions)

  const orNode = buildGroup(hasWordsArr, SearchOperator.OR, optionsCount)
  const andNode = buildGroup(hasAllWordsArr, SearchOperator.AND, optionsCount)
  const notNode = buildGroup(notWordsArr, SearchOperator.AND, optionsCount, '-')

  // Merge non-null nodes with AND
  const nodes = [exactMatch, orNode, andNode, notNode].filter(
    (node) => node !== null,
  ) as SearchASTNode[]
  if (!nodes.length) return null

  let ast: SearchASTNode = nodes[0]
  for (let i = 1; i < nodes.length; i++) {
    ast = {
      left: ast,
      operator: SearchOperator.AND,
      right: nodes[i],
    }
  }
  return ast
}

export const addQuotesToQuery = (query: string): string => {
  if (
    query &&
    query.length > 0 &&
    query.trim().includes(' ') &&
    query[0] !== '"' &&
    query[query.length - 1] !== '"'
  ) {
    return `"${query}"`
  }
  return query
}

export const removeQuotesFromQuery = (query: string): string => {
  if (
    query &&
    query.length > 0 &&
    query[0] === '"' &&
    query[query.length - 1] === '"'
  ) {
    return query.slice(1, -1)
  }
  return query
}

export const parseSearchQuery = (input: string): SearchOptions => {
  let ast
  try {
    ast = lucene.parse(input)
  } catch {
    return fallbackToExactMatch(input)
  }

  const components = extractComponents(ast as SearchASTNode)
  if (components) return components

  return fallbackToExactMatch(input)
}

export const searchOptionsToQuery = (searchOptions: SearchOptions): string => {
  const advancedSearchOptions: AdvancedSearchOptions = {
    exactMatch: searchOptions.getExactMatchStrsList()[0],
    includeAll: searchOptions.getIncludeAllStrsList().join(' '),
    includeAny: searchOptions.getIncludeSomeStrsList().join(' '),
    excludeAll: searchOptions.getExcludedStrsList().join(' '),
  }
  return lucene.toString(buildAstFromInputs(advancedSearchOptions) as AST)
}

const fallbackToExactMatch = (input: string): SearchOptions => {
  if (/\s/.test(input)) {
    return new SearchOptions().setExactMatchStrsList([input])
  } else {
    return new SearchOptions().setIncludeAllStrsList([input])
  }
}

const extractComponents = (ast: SearchASTNode): SearchOptions | null => {
  if (isIncludeAll(ast)) {
    const terms = flattenGroup(ast, SearchOperator.AND)
    if (terms && terms.every(isPlainTerm)) {
      return new SearchOptions().setIncludeAllStrsList(terms.map(getTermValue))
    } else {
      return null
    }
  }

  const topLevelQuery = flattenGroup(ast, SearchOperator.AND)
  if (!topLevelQuery) return null

  const result: SearchOptions = new SearchOptions()

  for (const group of topLevelQuery) {
    const parsed = classifyGroup(group)
    if (!parsed) return null

    // prevent duplicates (only one of each type)
    if (parsed.getExactMatchStrsList().length > 0) {
      if (result.getExactMatchStrsList().length > 0) return null // invalid query
      result.setExactMatchStrsList(parsed.getExactMatchStrsList())
    }
    if (parsed.getIncludeAllStrsList().length > 0) {
      if (result.getIncludeAllStrsList().length > 0) return null // invalid query
      result.setIncludeAllStrsList(parsed.getIncludeAllStrsList())
    }
    if (parsed.getIncludeSomeStrsList().length > 0) {
      if (result.getIncludeSomeStrsList().length > 0) return null // invalid query
      result.setIncludeSomeStrsList(parsed.getIncludeSomeStrsList())
    }
    if (parsed.getExcludedStrsList().length > 0) {
      if (result.getExcludedStrsList().length > 0) return null // invalid query
      result.setExcludedStrsList(parsed.getExcludedStrsList())
    }
  }

  return result
}

const classifyGroup = (node: SearchASTNode): SearchOptions | null => {
  if ('term' in node && node.term && !('operator' in node)) {
    if (isExcludeAll(node)) {
      return new SearchOptions().setExcludedStrsList([node.term])
    } else {
      return new SearchOptions().setExactMatchStrsList([node.term])
    }
  }

  if (
    'left' in node &&
    node.left &&
    'term' in node.left &&
    node.left.term &&
    !('operator' in node)
  ) {
    if (isExcludeAll(node)) {
      return new SearchOptions().setExcludedStrsList([node.left.term])
    } else {
      return new SearchOptions().setExactMatchStrsList([node.left.term])
    }
  }

  // Includes all
  if ('operator' in node && node.operator === SearchOperator.AND) {
    // check if this is an exclude all

    if (isExcludeAll(node)) {
      return new SearchOptions().setExcludedStrsList(flattenExcludeAll(node))
    } else {
      if (isIncludeAll(node)) {
        return new SearchOptions().setIncludeAllStrsList(
          flattenGroup(node, SearchOperator.AND)?.map(getTermValue) ?? [],
        )
      }
    }
    return null
  }

  // Includes some
  if ('operator' in node && node.operator === SearchOperator.OR) {
    const terms = flattenGroup(node, SearchOperator.OR)
    if (terms && terms.every(isPlainTerm)) {
      return new SearchOptions().setIncludeSomeStrsList(terms.map(getTermValue))
    }
    return null
  }

  return null
}

const flattenGroup = (
  node: SearchASTNode,
  operator: SearchOperator,
): SearchASTNode[] | null => {
  if (!('operator' in node)) return [node]
  if (node.operator !== operator) return [node]

  const left =
    'left' in node && node.left
      ? 'parenthesized' in node.left && node.left.parenthesized
        ? [node.left]
        : flattenGroup(node.left, operator)
      : null
  const right =
    'right' in node && node.right
      ? 'parenthesized' in node.right && node.right.parenthesized
        ? [node.right]
        : flattenGroup(node.right, operator)
      : null
  if (!left || !right) return null
  return [...left, ...right]
}

const isPlainTerm = (node: SearchASTNode): boolean => {
  return Boolean(
    ('term' in node &&
      node.term &&
      !('prefix' in node && node.prefix === '-')) ||
      ('left' in node &&
        node.left &&
        'term' in node.left &&
        node.left.term &&
        !('prefix' in node.left && node.left.prefix === '-')),
  )
}

const getTermValue = (node: SearchASTNode): string => {
  if ('term' in node && node.term) return node.term
  if ('left' in node && node.left && 'term' in node.left && node.left.term)
    return node.left.term
  return ''
}

const isIncludeAll = (ast: SearchASTNode): boolean => {
  if ('operator' in ast && ast.operator === SearchOperator.AND) {
    const nodes = flattenGroup(ast, SearchOperator.AND)
    return nodes !== null && nodes.every(isPlainTerm)
  }
  return false
}

const isExcludeAll = (ast: SearchASTNode): boolean => {
  if ('prefix' in ast && ast.prefix === '-') return true
  if (
    'left' in ast &&
    ast.left &&
    'prefix' in ast.left &&
    ast.left.prefix === '-'
  )
    return true
  if ('operator' in ast && ast.operator === SearchOperator.AND) {
    const nodes = flattenGroup(ast, SearchOperator.AND)
    return (
      nodes !== null &&
      nodes.every(
        (n) => 'prefix' in n && n.prefix === '-' && 'term' in n && n.term,
      )
    )
  }
  return false
}

const flattenExcludeAll = (ast: SearchASTNode): string[] => {
  if ('prefix' in ast && ast.prefix === '-')
    return ['term' in ast && ast.term ? ast.term : '']
  const nodes = flattenGroup(ast, SearchOperator.AND) ?? []
  return nodes.map((n) => ('term' in n && n.term ? n.term : ''))
}

export const getShouldExclude = (
  searchableText: string,
  searchOptions: SearchOptions,
): boolean => {
  return (
    searchOptions.getExcludedStrsList().length > 0 &&
    searchOptions
      .getExcludedStrsList()
      .some((str) => searchableText.includes(str.toLowerCase()))
  )
}

export const getShouldInclude = (
  searchableText: string,
  searchOptions: SearchOptions,
): boolean => {
  let isMatch = true
  if (
    searchOptions.getIncludeSomeStrsList().length > 0 &&
    !searchOptions
      .getIncludeSomeStrsList()
      .some((str) => searchableText.includes(str.toLowerCase()))
  ) {
    isMatch = false
  }
  if (
    searchOptions.getIncludeAllStrsList().length > 0 &&
    !searchOptions
      .getIncludeAllStrsList()
      .every((str) => searchableText.includes(str.toLowerCase()))
  ) {
    isMatch = false
  }
  if (
    searchOptions.getExactMatchStrsList().length > 0 &&
    !searchOptions
      .getExactMatchStrsList()
      .every((str) => searchableText.includes(str.toLowerCase()))
  ) {
    isMatch = false
  }
  return isMatch
}

export const getSearchQueryString = (
  query: string,
  advancedSearch?: AdvancedSearchOptions,
): string => {
  if (!advancedSearch) {
    const parsedQuery = parseSearchQuery(query.trim())
    return searchOptionsToQuery(parsedQuery)
  } else {
    const ast = buildAstFromInputs(advancedSearch)
    return lucene.toString(ast as AST)
  }
}
